import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { basisLabel, fmt, pct, signed, downloadCSV, heatColor } from "@/lib/utils-msip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, Download } from "lucide-react";

const Spark = ({ values }) => {
  const vals = values || [];
  if (vals.length < 2) return <span className="text-muted-foreground">—</span>;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 76 + 2},${20 - ((v - min) / range) * 16}`).join(" ");
  return (
    <svg width="80" height="22" className="inline-block">
      <polyline points={pts} fill="none" stroke="#0d765a" strokeWidth="1.5" />
    </svg>
  );
};

const COLS = [
  { key: "rto", label: "RTO" },
  { key: "district", label: "District" },
  { key: "tiv", label: "TIV", num: true },
  { key: "brand_units", label: "Units", num: true },
  { key: "share", label: "Share %", num: true, heat: true },
  { key: "delta_mom", label: "Δ MoM", num: true },
  { key: "rank", label: "Rank", num: true },
  { key: "rival_share", label: "Rival %", num: true },
  { key: "gap_to_rival", label: "Gap", num: true },
  { key: "unit_gap", label: "Unit Gap", num: true, heat: true },
];

export default function RTOExplorer() {
  const { filters } = useApp();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "tiv", dir: -1 });

  useEffect(() => {
    api
      .get(`/rtos?${qs({ brand: filters.brand, basis: filters.basis, period: filters.period, district: filters.district, territory_only: filters.territoryOnly })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [filters]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (search) r = r.filter((x) => x.rto.toLowerCase().includes(search.toLowerCase()) || (x.district || "").toLowerCase().includes(search.toLowerCase()));
    return [...r].sort((a, b) => {
      const av = a[sort.key] ?? -Infinity, bv = b[sort.key] ?? -Infinity;
      return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir;
    });
  }, [data, search, sort]);

  const heat = useMemo(() => {
    const out = {};
    for (const col of COLS.filter((c) => c.heat)) {
      const vals = rows.map((r) => r[col.key]).filter((v) => v != null);
      out[col.key] = { min: Math.min(...vals), max: Math.max(...vals) };
    }
    return out;
  }, [rows]);

  const exportCsv = () =>
    downloadCSV(
      `rto_explorer_${filters.brand}_${filters.period}.csv`,
      COLS.map((c) => c.label),
      rows.map((r) => COLS.map((c) => r[c.key])),
      filters.basis
    );

  return (
    <div className="space-y-4" data-testid="rto-explorer">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">RTO Explorer</h1>
        <span className="text-xs text-muted-foreground">{data?.period?.label} · {basisLabel(filters.basis)} · Rival: {data?.rival}</span>
        <div className="ml-auto flex gap-2">
          <Input placeholder="Search RTO / district…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-52 text-sm" data-testid="rto-search-input" />
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="rto-export-csv-btn">
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="border bg-card rounded-md overflow-auto max-h-[72vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card z-10 border-b">
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}
                  className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground cursor-pointer whitespace-nowrap select-none ${c.num ? "text-right" : "text-left"}`}
                  data-testid={`sort-${c.key}`}
                >
                  {c.label} <ArrowUpDown size={10} className="inline opacity-40" />
                </th>
              ))}
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground text-left">6-mo trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rto} className="border-b last:border-0 hover:bg-muted/50 transition-colors duration-200">
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                  <Link to={`/rto/${encodeURIComponent(r.rto)}`} className="hover:underline" data-testid={`rto-link-${r.rto}`}>{r.rto}</Link>
                  {r.is_state_row && <span className="ml-1.5 text-[10px] rounded bg-secondary px-1 py-0.5 text-muted-foreground">STATE</span>}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{r.district}</td>
                <td className="px-3 py-1.5 text-right num">{fmt(r.tiv)}</td>
                <td className="px-3 py-1.5 text-right num">{fmt(r.brand_units)}</td>
                <td className="px-3 py-1.5 text-right num font-semibold" style={{ background: heatColor(r.share, heat.share?.min, heat.share?.max) }}>{pct(r.share)}</td>
                <td className={`px-3 py-1.5 text-right num ${r.delta_mom > 0 ? "text-emerald-700" : r.delta_mom < 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.delta_mom != null ? signed(r.delta_mom) : "—"}</td>
                <td className="px-3 py-1.5 text-right num">{r.rank ? `#${r.rank}` : "—"}</td>
                <td className="px-3 py-1.5 text-right num text-muted-foreground">{pct(r.rival_share)}</td>
                <td className={`px-3 py-1.5 text-right num ${r.gap_to_rival >= 0 ? "text-emerald-700" : "text-red-600"}`}>{signed(r.gap_to_rival)}</td>
                <td className="px-3 py-1.5 text-right num" style={{ background: heatColor(r.unit_gap, heat.unit_gap?.min, heat.unit_gap?.max) }}>{fmt(r.unit_gap)}</td>
                <td className="px-3 py-1.5"><Spark values={r.spark} /></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-sm text-muted-foreground">No rows for this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
